import { Input, Menu, MenuItem, MenuTrigger } from '@affine/component';
import { SettingRow } from '@affine/component/setting-components';
import { useI18n } from '@affine/i18n';
import { type CSSProperties, useCallback } from 'react';

import type {
  PdfOcrPolicy,
  PdfOcrPolicyConfig,
} from './use-workspace-ai-settings';

const POLICY_OPTIONS: PdfOcrPolicy[] = [
  'manual',
  'auto_under_limit',
  'always_auto_within_limits',
  'disabled_visible',
];

const policyTriggerStyle: CSSProperties = {
  width: 220,
  maxWidth: '100%',
  minWidth: 160,
  height: 40,
  fontSize: 14,
  fontWeight: 600,
};

const policyTriggerContentStyle: CSSProperties = {
  display: 'block',
  flex: '1 1 auto',
  width: 'auto',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const policyMenuStyle: CSSProperties = {
  width: 220,
  maxWidth: 'calc(100vw - 32px)',
};

const policyMenuItemStyle: CSSProperties = {
  minHeight: 36,
  padding: '7px 8px',
  fontSize: 14,
  fontWeight: 500,
};

const selectedPolicyMenuItemStyle: CSSProperties = {
  background: 'var(--affine-hover-color)',
  color: 'var(--affine-primary-color)',
  fontWeight: 600,
};

const numberInputStyle: CSSProperties = { width: 96 };

export function PdfOcrPolicySettings({
  config,
  canEdit,
  isSaving,
  onChange,
}: {
  config: PdfOcrPolicyConfig;
  canEdit: boolean;
  isSaving: boolean;
  onChange: (config: Partial<PdfOcrPolicyConfig>) => Promise<void>;
}) {
  const t = useI18n();
  const disabled = !canEdit || isSaving;
  const showScanLimits =
    config.policy === 'auto_under_limit' ||
    config.policy === 'always_auto_within_limits';
  const showConfirmationThreshold = config.policy === 'auto_under_limit';
  const displayedPolicy =
    config.policy === 'disabled_hidden' ? 'disabled_visible' : config.policy;
  const selectedPolicyLabel = policyLabel(t, displayedPolicy || 'manual');

  const updateNumber = useCallback(
    (field: keyof Omit<PdfOcrPolicyConfig, 'policy'>, value: string) => {
      const parsed = Number(value);
      if (!Number.isInteger(parsed) || parsed < 1) return;
      void onChange({ [field]: parsed });
    },
    [onChange]
  );

  return (
    <div data-testid="pdf-ocr-policy-settings">
      <SettingRow
        name={
          t[
            'com.streamlain.settings.workspace.indexer-embedding.pdf-ocr.title'
          ]?.() ?? 'PDF image text recognition'
        }
        desc={
          t[
            'com.streamlain.settings.workspace.indexer-embedding.pdf-ocr.description'
          ]?.() ??
          'Text layers are indexed automatically. Image text OCR follows this workspace policy.'
        }
      >
        <Menu
          items={POLICY_OPTIONS.map(policy => {
            const isSelected = displayedPolicy === policy;
            return (
              <MenuItem
                key={policy}
                selected={isSelected}
                onSelect={() => {
                  void onChange({ policy });
                }}
                style={{
                  ...policyMenuItemStyle,
                  ...(isSelected ? selectedPolicyMenuItemStyle : {}),
                }}
                data-testid={`pdf-ocr-policy-option-${policy}`}
              >
                {policyLabel(t, policy)}
              </MenuItem>
            );
          })}
          contentOptions={{
            align: 'end',
            style: policyMenuStyle,
          }}
        >
          <MenuTrigger
            block
            disabled={disabled}
            data-testid="pdf-ocr-policy-select"
            style={policyTriggerStyle}
            contentStyle={policyTriggerContentStyle}
          >
            {selectedPolicyLabel}
          </MenuTrigger>
        </Menu>
      </SettingRow>

      {showScanLimits ? (
        <>
          <SettingRow
            name={
              t[
                'com.streamlain.settings.workspace.indexer-embedding.pdf-ocr.max-pages'
              ]?.() ?? 'Maximum OCR pages per PDF'
            }
          >
            <Input
              type="number"
              min={1}
              value={String(config.maxCandidatePages)}
              disabled={disabled}
              onChange={value => updateNumber('maxCandidatePages', value)}
              style={numberInputStyle}
            />
          </SettingRow>

          <SettingRow
            name={
              t[
                'com.streamlain.settings.workspace.indexer-embedding.pdf-ocr.max-calls'
              ]?.() ?? 'Maximum provider calls per job'
            }
          >
            <Input
              type="number"
              min={1}
              value={String(config.maxProviderCalls)}
              disabled={disabled}
              onChange={value => updateNumber('maxProviderCalls', value)}
              style={numberInputStyle}
            />
          </SettingRow>

          <SettingRow
            name={
              t[
                'com.streamlain.settings.workspace.indexer-embedding.pdf-ocr.max-credits'
              ]?.() ?? 'Maximum credits per PDF'
            }
          >
            <Input
              type="number"
              min={1}
              value={String(config.maxCreditsPerPdf)}
              disabled={disabled}
              onChange={value => updateNumber('maxCreditsPerPdf', value)}
              style={numberInputStyle}
            />
          </SettingRow>
        </>
      ) : null}

      {showConfirmationThreshold ? (
        <SettingRow
          name={
            t[
              'com.streamlain.settings.workspace.indexer-embedding.pdf-ocr.confirmation-threshold'
            ]?.() ?? 'Confirmation threshold'
          }
          desc={
            t[
              'com.streamlain.settings.workspace.indexer-embedding.pdf-ocr.confirmation-threshold.desc'
            ]?.() ??
            'Manual starts above this candidate page count require confirmation.'
          }
        >
          <Input
            type="number"
            min={1}
            value={String(config.confirmationThresholdPages)}
            disabled={disabled}
            onChange={value =>
              updateNumber('confirmationThresholdPages', value)
            }
            style={numberInputStyle}
          />
        </SettingRow>
      ) : null}
    </div>
  );
}

function policyLabel(
  t: ReturnType<typeof useI18n>,
  policy: PdfOcrPolicy
): string {
  return (
    t[
      `com.streamlain.settings.workspace.indexer-embedding.pdf-ocr.policy.${policy}` as keyof typeof t
    ]?.() ?? policy
  );
}
